import { useCallback, useRef, useState, type ReactNode } from "react";

import { Trans, useLingui } from "@lingui/react/macro";

import type { PendingChange, PendingChoice } from "./confirm-card-action";
import { Button } from "@/components/ui/button";
import { ResponsiveSheet } from "@/components/ui/responsive-sheet";
import { tLocalized, type LocalizedString } from "@/types/localized-string";

/**
 * An EARLIER change of the subscription still held by Stripe for a payment the
 * bank wants confirmed (409 `payment_processing` with its secret). Before
 * anything is confirmed the customer sees what it is — it may be an older
 * change than the one being made now — and picks: pay it, or discard it.
 * Closing the sheet does neither.
 */
export function usePendingPaymentPrompt(): {
  ask: (pending: PendingChange) => Promise<PendingChoice>;
  sheet: ReactNode;
} {
  const { t, i18n } = useLingui();
  const [pending, setPending] = useState<PendingChange | null>(null);
  const resolveRef = useRef<((choice: PendingChoice) => void) | null>(null);

  const ask = useCallback(
    (next: PendingChange) =>
      new Promise<PendingChoice>((resolve) => {
        resolveRef.current = resolve;
        setPending(next);
      }),
    [],
  );
  const choose = (choice: PendingChoice) => {
    resolveRef.current?.(choice);
    resolveRef.current = null;
    setPending(null);
  };

  const change = pending?.change ?? null;
  const planName =
    change?.planName && typeof change.planName === "object"
      ? tLocalized(change.planName as LocalizedString, i18n.locale)
      : (change?.planCode ?? "");
  const amount =
    change?.amountCents != null && change.currency
      ? new Intl.NumberFormat(i18n.locale, { style: "currency", currency: change.currency }).format(
          change.amountCents / 100,
        )
      : null;
  const interval =
    change?.interval === "annual"
      ? t`Billed yearly`
      : change?.interval === "monthly"
        ? t`Billed monthly`
        : null;

  const sheet = (
    <ResponsiveSheet
      open={pending !== null}
      onClose={() => choose("cancel")}
      title={<Trans>A payment is waiting for your confirmation</Trans>}
      description={
        <Trans>
          An earlier change to this subscription is waiting for your bank's confirmation. Pay it, or
          discard it to continue.
        </Trans>
      }
      width="sm"
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row">
          <Button
            variant="outline"
            className="min-h-[44px] flex-1"
            onClick={() => choose("discard")}
          >
            <Trans>Discard</Trans>
          </Button>
          <Button className="min-h-[44px] flex-1" onClick={() => choose("pay")}>
            <Trans>Pay</Trans>
          </Button>
        </div>
      }
    >
      <dl className="bg-muted/30 m-5 space-y-2 rounded-xl border p-4 text-sm">
        {planName && (
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">
              <Trans>Plan</Trans>
            </dt>
            <dd className="text-foreground text-right font-medium">{planName}</dd>
          </div>
        )}
        {change?.quantity != null && (
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">
              <Trans>Doctors</Trans>
            </dt>
            <dd className="text-foreground text-right font-medium tabular-nums">
              {change.quantity}
            </dd>
          </div>
        )}
        {interval && (
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">
              <Trans>Billing</Trans>
            </dt>
            <dd className="text-foreground text-right font-medium">{interval}</dd>
          </div>
        )}
        {amount && (
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">
              <Trans>Amount due now</Trans>
            </dt>
            <dd className="text-foreground text-right font-semibold tabular-nums">{amount}</dd>
          </div>
        )}
      </dl>
    </ResponsiveSheet>
  );

  return { ask, sheet };
}
